import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ACTION_COLOR = {
  asset_created: "text-[#2D7A4F]",
  asset_updated: "text-[#3A3A3A]",
  asset_deleted: "text-[#B91C1C]",
  assets_bulk_deleted: "text-[#B91C1C]",
  asset_status_changed: "text-[#B45309]",
  asset_reassigned: "text-[#B45309]",
  asset_retired: "text-[#B45309] font-medium",
  assets_imported: "text-[#1A1A1A]",
  assets_exported: "text-[#1A1A1A]",
  category_created: "text-[#3A3A3A]",
  category_deleted: "text-[#B91C1C]",
  software_created: "text-[#2D7A4F]",
  software_deleted: "text-[#B91C1C]",
  mfa_enabled: "text-[#2D7A4F]",
  mfa_disabled: "text-[#B91C1C]",
  m365_sync_success: "text-[#2D7A4F]",
  m365_sync_failed: "text-[#B91C1C]",
  adobe_sync_success: "text-[#2D7A4F]",
  adobe_sync_failed: "text-[#B91C1C]",
};

export default function AuditLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [actions, setActions] = useState([]);

  // Debounce search input to avoid firing on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/audit-logs/actions").then(({ data }) => setActions(data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    const params = { limit: 500 };
    if (debouncedSearch) params.search = debouncedSearch;
    if (actionFilter !== "all") params.action = actionFilter;
    api
      .get("/audit-logs", { params })
      .then(({ data }) => setLogs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin, debouncedSearch, actionFilter]);

  const hasActiveFilter = debouncedSearch || actionFilter !== "all";
  const clearAll = () => { setSearch(""); setActionFilter("all"); };

  const summary = useMemo(() => {
    if (loading) return "";
    if (!hasActiveFilter) return `${logs.length} event${logs.length === 1 ? "" : "s"}`;
    return `${logs.length} match${logs.length === 1 ? "" : "es"}`;
  }, [logs.length, hasActiveFilter, loading]);

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div data-testid="audit-page">
      <PageHeader
        eyebrow="[04] Forensics"
        title="Audit Log"
        subtitle="An immutable record of every action taken in this register."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {/* Search + filter toolbar */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
            <Input
              data-testid="audit-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by action, operator, asset tag, email, method, notes…"
              className="pl-10 pr-9 rounded-none bg-white border-[#D6CFC1] font-mono text-sm h-10"
            />
            {search && (
              <button
                data-testid="audit-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A7A7A] hover:text-[#0F0F0F]"
              ><X className="w-4 h-4" /></button>
            )}
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger
              data-testid="audit-action-filter"
              className="w-full sm:w-64 rounded-none bg-white border-[#D6CFC1] font-mono text-sm h-10"
            ><SelectValue placeholder="Filter by action" /></SelectTrigger>
            <SelectContent className="rounded-none bg-white border-[#D6CFC1] max-h-80">
              <SelectItem value="all">All actions</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilter && (
            <button
              data-testid="audit-clear-filters"
              onClick={clearAll}
              className="font-mono text-xs uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F] px-3"
            >Clear filters</button>
          )}
        </div>

        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]" data-testid="audit-summary">
          {summary}
        </div>

        <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="border-b border-[#E5E0D5]">
                <Th>Timestamp</Th>
                <Th>Operator</Th>
                <Th>Action</Th>
                <Th>Asset</Th>
                <Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center font-mono text-xs text-[#9A9A9A]">
                  {hasActiveFilter ? "No events match your search." : "No audit events yet."}
                </td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3]" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#5A5A5A] whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#3A3A3A]">{l.user_email}</td>
                  <td className={`px-4 py-3 font-mono text-xs ${ACTION_COLOR[l.action] || "text-[#3A3A3A]"}`}>{l.action}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#7A7A7A]">{l.asset_id ? l.asset_id.slice(-8) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#7A7A7A]">
                    {l.details && Object.keys(l.details).length > 0
                      ? Object.entries(l.details).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] font-medium">{children}</th>;
}
